/**
 * Super Admin › Mensajes del sistema.
 *
 * Los siete mensajes que la plataforma le manda a sus clientes: el texto, por
 * dónde sale, sus reglas y su webhook. Hasta S79 ese contenido estaba escrito
 * dentro del código y cambiarle una coma exigía un despliegue.
 *
 * LO QUE NO SE PUEDE HACER SE DICE, NO SE ESCONDE
 * ===============================================
 *
 * Un interruptor apagado sin explicación se lee como un fallo. Aquí, cuando algo
 * no se puede tocar, aparece deshabilitado CON el motivo al lado:
 *
 * - «Recuperar contraseña» no ofrece WhatsApp, y el motivo se lee: un enlace que
 *   restablece una contraseña quedaría en el historial de un chat.
 * - Bienvenida y recuperar contraseña no se pueden apagar: sin ellos, un cliente
 *   nuevo no entra o uno existente no vuelve a entrar.
 * - WhatsApp no se puede encender mientras no se elija de qué negocio sale, y se
 *   explica por qué: la plataforma no tiene número propio.
 *
 * EL REGISTRO ES LA MITAD DE LA PANTALLA, NO UN EXTRA
 * ===================================================
 *
 * Enseña también lo que NO se mandó y por qué. La pregunta que uno se hace de
 * verdad no es «¿cuántos correos salieron?» sino «¿por qué este cliente no
 * recibió el suyo?», y una lista de éxitos no la responde.
 */
import { useState, useEffect, useCallback } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import FieldHelp from '@/components/help/FieldHelp';
import {
  Mail, MessageSquare, Save, Send, Webhook, KeyRound, History, Info, Lock, Megaphone,
  Eye, Copy, CheckCircle2, AlertTriangle,
} from 'lucide-react';

const NATURALEZA = {
  accion: 'Sale solo, en cuanto pasa',
  fecha: 'Lo revisa el repaso diario',
  estado: 'Sale cuando tú cambias el estado de la cuenta',
  manual: 'Lo mandas tú cuando quieras',
};

const ESTADO_COLOR = {
  sent: 'text-emerald-600',
  failed: 'text-red-600',
  skipped: 'text-amber-600',
};

const ESTADO_TEXTO = {
  sent: 'Enviado',
  failed: 'Falló',
  skipped: 'No se mandó',
};

export default function PlatformMessages() {
  const [eventos, setEventos] = useState([]);
  const [whatsapp, setWhatsapp] = useState({});
  const [negocios, setNegocios] = useState([]);
  const [activo, setActivo] = useState(null);
  const [borrador, setBorrador] = useState({});
  const [registro, setRegistro] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [secreto, setSecreto] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const r = await api.get('/admin/mensajes-del-sistema/eventos');
      setEventos(r.data.eventos || []);
      setWhatsapp(r.data.whatsapp || {});
      setActivo((prev) => prev || (r.data.eventos || [])[0]?.clave || null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCargando(false);
    }
  }, []);

  const cargarRegistro = useCallback(async () => {
    try {
      const r = await api.get('/admin/mensajes-del-sistema/registro', { params: { limite: 100 } });
      setRegistro(r.data.registro || []);
    } catch (e) {
      // El registro es informativo: si falla, la pantalla sigue siendo usable.
    }
  }, []);

  useEffect(() => { cargar(); cargarRegistro(); }, [cargar, cargarRegistro]);

  useEffect(() => {
    (async () => {
      try {
        // `/admin/tenants` devuelve una LISTA pelada y su tope se llama `limit`
        // (máximo 100), no `page_size`. Escrito de memoria, el selector habría
        // salido vacío para siempre sin ningún error visible.
        const r = await api.get('/admin/tenants', { params: { limit: 100 } });
        setNegocios(Array.isArray(r.data) ? r.data : []);
      } catch (e) { /* el selector de emisor simplemente saldrá vacío */ }
    })();
  }, []);

  const evento = eventos.find((e) => e.clave === activo);
  const valor = (campo) => (borrador[campo] !== undefined ? borrador[campo] : evento?.[campo]);
  const cambiar = (campo, v) => setBorrador((b) => ({ ...b, [campo]: v }));
  const sucio = Object.keys(borrador).length > 0;

  const guardar = async () => {
    if (!evento) return;
    setGuardando(true);
    try {
      const r = await api.put(`/admin/mensajes-del-sistema/eventos/${evento.clave}`, borrador);
      setEventos((prev) => prev.map((e) => (e.clave === evento.clave ? r.data : e)));
      setBorrador({});
      toast.success('Guardado. Se aplica al siguiente envío.');
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGuardando(false);
    }
  };

  const probar = async () => {
    try {
      const r = await api.post(`/admin/mensajes-del-sistema/eventos/${evento.clave}/probar`);
      if (r.data.ok) toast.success(`Enviado a ${r.data.destino}. Revisa tu bandeja.`);
      else toast.error(r.data.saltado || 'No salió por ningún canal. Mira el registro abajo.');
      cargarRegistro();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const generarSecreto = async () => {
    try {
      const r = await api.post(`/admin/mensajes-del-sistema/eventos/${evento.clave}/secreto`);
      setSecreto(r.data);
      setEventos((prev) => prev.map((e) => (e.clave === evento.clave ? { ...e, webhook_tiene_secreto: true } : e)));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const fijarEmisor = async (tenantId) => {
    try {
      await api.put('/admin/mensajes-del-sistema/emisor-whatsapp', { tenant_id: tenantId || null });
      toast.success(tenantId ? 'Listo. Ya puedes encender WhatsApp en los mensajes que lo admitan.' : 'WhatsApp de plataforma desconectado.');
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (cargando) return <div className="p-8 text-sm text-[#64748B]">Cargando mensajes…</div>;

  const emisorPuesto = !!whatsapp?.negocio_emisor;

  return (
    <div className="p-4 md:p-8 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Mensajes del sistema</h1>
        <p className="text-sm text-[#64748B] mt-1 max-w-3xl">
          Lo que la plataforma le escribe a tus clientes: bienvenida, recuperar contraseña, avisos de
          cobro, suspensión, reactivación, aniversario y promociones. Cambia el texto aquí y se aplica
          al siguiente envío, sin tocar nada más.
        </p>
      </div>

      {/* ── el WhatsApp de la plataforma ────────────────────────────── */}
      <div className="rounded-lg border border-[#E2E8F0] dark:border-white/10 p-4 space-y-3">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <MessageSquare className="w-4 h-4" /> WhatsApp de la plataforma
        </div>
        <p className="text-xs text-[#64748B] max-w-3xl">{whatsapp?.porque}</p>
        <div className="flex flex-wrap items-center gap-3">
          <Select value={whatsapp?.negocio_emisor?.id || ''} onValueChange={fijarEmisor}>
            <SelectTrigger className="w-[320px]">
              <SelectValue placeholder="Elige el negocio desde el que saldrán" />
            </SelectTrigger>
            <SelectContent>
              {negocios.map((n) => (
                <SelectItem key={n.id} value={n.id}>{n.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {emisorPuesto && (
            <Button variant="ghost" size="sm" onClick={() => fijarEmisor(null)}>Quitar</Button>
          )}
          {!emisorPuesto && (
            <span className="text-xs text-amber-600">
              Mientras no elijas uno, estos mensajes solo salen por correo.
            </span>
          )}
        </div>
      </div>

      <Tabs value={activo || ''} onValueChange={(v) => { setActivo(v); setBorrador({}); setSecreto(null); }}>
        <TabsList className="flex flex-wrap h-auto">
          {eventos.map((e) => (
            <TabsTrigger key={e.clave} value={e.clave} className="text-xs">
              {e.nombre}
              {!e.enabled && <span className="ml-1 text-[10px] text-amber-600">(apagado)</span>}
            </TabsTrigger>
          ))}
        </TabsList>

        {eventos.map((e) => (
          <TabsContent key={e.clave} value={e.clave} className="space-y-5 pt-4">
            {evento?.clave === e.clave && (
              <>
                {/* qué es y cuándo sale */}
                <div className="rounded-lg bg-[#F8FAFC] dark:bg-white/5 p-4 text-sm space-y-1">
                  <div className="flex items-center gap-2 font-medium"><Info className="w-4 h-4" /> {e.nombre}</div>
                  <p className="text-[#64748B] text-xs">{e.ayuda}</p>
                  <p className="text-[#64748B] text-xs">{NATURALEZA[e.naturaleza]}</p>
                </div>

                {/* encendido + canales */}
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="rounded-lg border border-[#E2E8F0] dark:border-white/10 p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <FieldHelp
                        label="Este mensaje está activo"
                        htmlFor={`activo-${e.clave}`}
                        help="Apagado, el sistema deja de mandarlo. Lo que ya se mandó no se borra."
                      >
                        <span className="text-xs text-[#64748B]">
                          {valor('enabled') ? 'Sí, se manda' : 'No, está apagado'}
                        </span>
                      </FieldHelp>
                      <Switch
                        id={`activo-${e.clave}`}
                        checked={!!valor('enabled')}
                        disabled={!e.se_puede_apagar}
                        onCheckedChange={(v) => cambiar('enabled', v)}
                      />
                    </div>
                    {!e.se_puede_apagar && (
                      <p className="text-xs text-[#64748B] flex items-start gap-1">
                        <Lock className="w-3 h-3 mt-0.5 shrink-0" /> {e.apagado_bloqueado_porque}
                      </p>
                    )}
                  </div>

                  <div className="rounded-lg border border-[#E2E8F0] dark:border-white/10 p-4 space-y-3">
                    <div className="text-xs font-medium text-[#64748B]">Por dónde sale</div>
                    {['email', 'whatsapp'].map((canal) => {
                      const admitido = e.canales_permitidos.includes(canal);
                      const faltaEmisor = canal === 'whatsapp' && !emisorPuesto;
                      const puesto = (valor('channels') || []).includes(canal);
                      return (
                        <div key={canal} className="flex items-center justify-between gap-3">
                          <span className="text-sm flex items-center gap-2">
                            {canal === 'email' ? <Mail className="w-4 h-4" /> : <MessageSquare className="w-4 h-4" />}
                            {canal === 'email' ? 'Correo' : 'WhatsApp'}
                          </span>
                          <Switch
                            checked={puesto}
                            disabled={!admitido || faltaEmisor}
                            onCheckedChange={(v) => {
                              const actuales = new Set(valor('channels') || []);
                              if (v) actuales.add(canal); else actuales.delete(canal);
                              cambiar('channels', [...actuales]);
                            }}
                          />
                        </div>
                      );
                    })}
                    {!e.canales_permitidos.includes('whatsapp') && (
                      <p className="text-xs text-[#64748B] flex items-start gap-1">
                        <Lock className="w-3 h-3 mt-0.5 shrink-0" /> {e.canal_bloqueado_porque}
                      </p>
                    )}
                    {e.canales_permitidos.includes('whatsapp') && !emisorPuesto && (
                      <p className="text-xs text-amber-600">
                        Para encender WhatsApp, elige arriba desde qué negocio sale.
                      </p>
                    )}
                  </div>
                </div>

                {/* el texto */}
                <div className="rounded-lg border border-[#E2E8F0] dark:border-white/10 p-4 space-y-4">
                  <FieldHelp
                    label="Asunto del correo"
                    htmlFor={`asunto-${e.clave}`}
                    help="Lo primero que ve el cliente en su bandeja. Puedes usar las etiquetas de abajo."
                  >
                    <Input
                      id={`asunto-${e.clave}`}
                      value={valor('subject') || ''}
                      onChange={(ev) => cambiar('subject', ev.target.value)}
                    />
                  </FieldHelp>

                  <FieldHelp
                    label="Mensaje"
                    htmlFor={`cuerpo-${e.clave}`}
                    help="Escribe normal, sin código. Deja una línea en blanco para separar párrafos; las direcciones de internet se vuelven enlaces solas."
                  >
                    <Textarea
                      id={`cuerpo-${e.clave}`}
                      rows={10}
                      value={valor('body') || ''}
                      onChange={(ev) => cambiar('body', ev.target.value)}
                    />
                  </FieldHelp>

                  <VistaPreviaCorreo clave={e.clave}
                                     asunto={valor('subject') || ''}
                                     cuerpo={valor('body') || ''} />

                  <div>
                    <div className="text-xs font-medium text-[#64748B] mb-2">
                      Etiquetas que puedes usar (haz clic para copiarla al final del mensaje)
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {(e.variables || []).map((v) => (
                        <button
                          key={v}
                          type="button"
                          className="text-xs px-2 py-1 rounded border border-[#E2E8F0] dark:border-white/10 hover:bg-[#F1F5F9] dark:hover:bg-white/10"
                          onClick={() => cambiar('body', `${valor('body') || ''}{${v}}`)}
                        >{`{${v}}`}</button>
                      ))}
                    </div>
                    <p className="text-xs text-[#64748B] mt-2">
                      Si escribes una etiqueta que no existe, saldrá tal cual en el mensaje. Es a
                      propósito: así la ves en la prueba y la corriges, en vez de que el cliente
                      reciba un hueco.
                    </p>
                  </div>
                </div>

                {/* reglas propias del evento */}
                {(e.forma_de_las_reglas || []).length > 0 && (
                  <div className="rounded-lg border border-[#E2E8F0] dark:border-white/10 p-4 space-y-4">
                    <div className="text-sm font-semibold">Reglas de este mensaje</div>
                    {e.forma_de_las_reglas.map((r) => {
                      const reglas = valor('rules') || {};
                      const actual = reglas[r.clave] !== undefined ? reglas[r.clave] : r.default;
                      const set = (v) => cambiar('rules', { ...reglas, [r.clave]: v });
                      return (
                        <FieldHelp key={r.clave} label={r.etiqueta} htmlFor={`regla-${r.clave}`} help={r.ayuda}>
                          {r.tipo === 'boolean' ? (
                            <Switch id={`regla-${r.clave}`} checked={!!actual} onCheckedChange={set} />
                          ) : r.tipo === 'dias_lista' ? (
                            <Input
                              id={`regla-${r.clave}`}
                              value={Array.isArray(actual) ? actual.join(', ') : (actual ?? '')}
                              placeholder="7, 1"
                              onChange={(ev) => set(
                                ev.target.value.split(',').map((x) => parseInt(x.trim(), 10)).filter((x) => !Number.isNaN(x)),
                              )}
                            />
                          ) : (
                            <Input
                              id={`regla-${r.clave}`}
                              type="number"
                              value={actual ?? ''}
                              onChange={(ev) => set(ev.target.value === '' ? null : Number(ev.target.value))}
                            />
                          )}
                        </FieldHelp>
                      );
                    })}
                  </div>
                )}

                {/* webhook */}
                <div className="rounded-lg border border-[#E2E8F0] dark:border-white/10 p-4 space-y-4">
                  <div className="flex items-center gap-2 text-sm font-semibold">
                    <Webhook className="w-4 h-4" /> Avisar a otro sistema
                  </div>
                  {e.admite_webhook === false ? (
                    // Se dice el motivo en vez de enseñar un interruptor que el
                    // servidor va a rechazar.
                    <p className="text-xs text-[#64748B] flex items-start gap-1">
                      <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {e.webhook_bloqueado_porque}
                    </p>
                  ) : (
                  <>
                  <p className="text-xs text-[#64748B] max-w-3xl">
                    Cada vez que ocurra este evento le mandamos los datos a una dirección tuya, por si
                    quieres engancharlo a otra herramienta. Sale aunque los canales estén apagados:
                    puede ser la única entrega que quieras para este mensaje.
                    <br />
                    Los enlaces y datos delicados van ocultos en el aviso, y la dirección debe ser
                    https pública — no se permite apuntar a la red interna del servidor.
                  </p>
                  <FieldHelp
                    label="Dirección a la que avisamos"
                    htmlFor={`webhook-${e.clave}`}
                    help="La URL que te dé la otra herramienta. Si no usas ninguna, deja esto vacío."
                  >
                    <Input
                      id={`webhook-${e.clave}`}
                      placeholder="https://…"
                      value={valor('webhook_url') || ''}
                      onChange={(ev) => cambiar('webhook_url', ev.target.value)}
                    />
                  </FieldHelp>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Avisar a esa dirección</span>
                    <Switch
                      checked={!!valor('webhook_enabled')}
                      onCheckedChange={(v) => cambiar('webhook_enabled', v)}
                    />
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <Button variant="outline" size="sm" onClick={generarSecreto}>
                      <KeyRound className="w-4 h-4 mr-1" />
                      {e.webhook_tiene_secreto ? 'Generar una clave nueva' : 'Generar clave de firma'}
                    </Button>
                    <span className="text-xs text-[#64748B]">
                      {e.webhook_tiene_secreto
                        ? 'Ya hay clave: cada aviso va firmado. Genera una nueva solo si la perdiste.'
                        : 'Se genera sola al encender el aviso. Este botón es por si quieres verla antes.'}
                    </span>
                  </div>
                  {secreto && (
                    <div className="rounded bg-amber-50 dark:bg-amber-500/10 border border-amber-300 p-3 text-xs space-y-2">
                      <div className="font-semibold">{secreto.guardalo}</div>
                      <code className="block break-all bg-white dark:bg-black/30 p-2 rounded">{secreto.secreto}</code>
                      <div className="text-[#64748B]">
                        Cabecera: <code>{secreto.cabecera}</code>. {secreto.como_verificar}
                      </div>
                    </div>
                  )}
                  </>
                  )}
                </div>

                <div className="flex flex-wrap gap-3">
                  <Button onClick={guardar} disabled={!sucio || guardando}>
                    <Save className="w-4 h-4 mr-1" /> {guardando ? 'Guardando…' : 'Guardar'}
                  </Button>
                  <Button variant="outline" onClick={probar}>
                    <Send className="w-4 h-4 mr-1" /> Mandarme una prueba
                  </Button>
                  {e.clave === 'promotion' && <PromocionRapida onHecho={cargarRegistro} />}
                  {sucio && <span className="text-xs text-amber-600 self-center">Tienes cambios sin guardar</span>}
                </div>
              </>
            )}
          </TabsContent>
        ))}
      </Tabs>

      <PlantillasWhatsApp />

      {/* ── registro ─────────────────────────────────────────────────── */}
      <div className="rounded-lg border border-[#E2E8F0] dark:border-white/10 p-4">
        <div className="flex items-center gap-2 text-sm font-semibold mb-1">
          <History className="w-4 h-4" /> Qué se ha mandado
        </div>
        <p className="text-xs text-[#64748B] mb-3">
          Incluye lo que NO se mandó y por qué, que suele ser lo que uno viene a buscar.
        </p>
        {registro.length === 0 ? (
          <p className="text-xs text-[#64748B]">Todavía no hay envíos registrados.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-[#64748B]">
                <tr className="text-left">
                  <th className="py-2 pr-4">Cuándo</th>
                  <th className="py-2 pr-4">Mensaje</th>
                  <th className="py-2 pr-4">Negocio</th>
                  <th className="py-2 pr-4">Canal</th>
                  <th className="py-2 pr-4">Resultado</th>
                  <th className="py-2">Detalle</th>
                </tr>
              </thead>
              <tbody>
                {registro.map((f, i) => (
                  <tr key={i} className="border-t border-[#E2E8F0] dark:border-white/10">
                    <td className="py-2 pr-4 whitespace-nowrap">
                      {f.momento ? new Date(f.momento).toLocaleString('es-MX') : '—'}
                    </td>
                    <td className="py-2 pr-4">{f.nombre_del_evento}</td>
                    <td className="py-2 pr-4">{f.negocio || f.correo || '—'}</td>
                    <td className="py-2 pr-4">{f.canal}</td>
                    <td className={`py-2 pr-4 font-medium ${ESTADO_COLOR[f.estado] || ''}`}>
                      {ESTADO_TEXTO[f.estado] || f.estado}
                    </td>
                    <td className="py-2 text-[#64748B]">{f.detalle || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/** El correo tal como le llega al cliente, mientras lo escribes.
 *
 * Va en un `iframe` con `srcDoc` y NO inyectado en la página: el correo trae su
 * propio `<body>` y sus estilos, y metido en el DOM del panel heredaría los
 * estilos de la app —se vería distinto aquí que en el buzón, que es justo lo que
 * una vista previa no debe hacer—. El `sandbox` sin `allow-scripts` es cinturón
 * de seguridad: aunque alguien lograra colar un script en el texto, aquí no
 * corre.
 */
function VistaPreviaCorreo({ clave, asunto, cuerpo }) {
  const [html, setHtml] = useState('');
  const [abierta, setAbierta] = useState(true);
  const [cargando, setCargando] = useState(false);

  useEffect(() => {
    if (!abierta) return undefined;
    // Medio segundo de espera: sin esto se pediría una vista previa por cada
    // tecla y el panel iría a tirones mientras escribes.
    const t = setTimeout(async () => {
      setCargando(true);
      try {
        const r = await api.post(`/admin/mensajes-del-sistema/eventos/${clave}/vista-previa`,
                                 { subject: asunto, body: cuerpo });
        setHtml(r.data.html || '');
      } catch (e) {
        setHtml('');
      } finally {
        setCargando(false);
      }
    }, 500);
    return () => clearTimeout(t);
  }, [clave, asunto, cuerpo, abierta]);

  return (
    <div className="rounded-lg border border-[#E2E8F0] dark:border-white/10 overflow-hidden">
      <button type="button" onClick={() => setAbierta((v) => !v)}
              className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium hover:bg-[#F8FAFC] dark:hover:bg-white/5">
        <span className="flex items-center gap-2"><Eye className="w-4 h-4" /> Así lo verá tu cliente</span>
        <span className="text-xs text-[#64748B]">
          {cargando ? 'actualizando…' : (abierta ? 'ocultar' : 'ver')}
        </span>
      </button>
      {abierta && (
        <div className="border-t border-[#E2E8F0] dark:border-white/10 bg-[#F1F5F9] p-3">
          <p className="text-xs text-[#64748B] mb-2">
            Con datos de ejemplo. Las etiquetas se rellenan solas al enviarlo de verdad.
          </p>
          <iframe
            title="Vista previa del correo"
            srcDoc={html}
            sandbox=""
            className="w-full rounded-md bg-white"
            style={{ height: 720, border: '1px solid #E2E8F0' }}
          />
        </div>
      )}
    </div>
  );
}

/** Las plantillas que hay que dar de alta en Meta para que WhatsApp funcione.
 *
 * Existe porque con WhatsApp Business API un evento sin plantilla aprobada
 * sencillamente NO se entrega, y eso se descubre tarde y en silencio. Aquí se
 * dice cuáles faltan y se entrega el contenido listo para copiar, en vez de
 * dejar al dueño traduciendo campos a mano en el formulario de Meta.
 */
function PlantillasWhatsApp() {
  const [datos, setDatos] = useState(null);
  const [copiada, setCopiada] = useState(null);
  const [enviando, setEnviando] = useState(false);

  const cargar = async () => {
    try {
      const r = await api.get('/admin/mensajes-del-sistema/plantillas-whatsapp');
      setDatos(r.data);
    } catch (e) { /* panel informativo: si falla, no estorba */ }
  };

  useEffect(() => { cargar(); }, []);

  if (!datos) return null;

  const enviarAMeta = async (clave = null) => {
    setEnviando(true);
    try {
      const { data } = await api.post(
        '/admin/mensajes-del-sistema/plantillas-whatsapp/enviar-a-meta',
        clave ? { clave } : {},
      );
      toast.success(data.aviso || `Enviadas ${data.enviadas_ok}/${data.total}`);
      const fallos = (data.resultados || []).filter(r => !r.ok);
      fallos.slice(0, 3).forEach(f => toast.error(`${f.clave || f.name}: ${f.error || 'falló'}`));
      await cargar();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo enviar a Meta');
    } finally {
      setEnviando(false);
    }
  };

  const copiar = async (p) => {
    const texto = [
      `Nombre: ${p.name}`,
      `Idioma: ${p.language}`,
      `Categoría: ${p.category}`,
      '',
      'Cuerpo:',
      p.components.find((c) => c.type === 'BODY')?.text || '',
    ].join('\n');
    try {
      await navigator.clipboard.writeText(texto);
      setCopiada(p.name);
      setTimeout(() => setCopiada(null), 2000);
      toast.success('Copiado. Pégalo en el Administrador de WhatsApp de Meta.');
    } catch (e) {
      toast.error('Tu navegador no dejó copiar. Selecciona el texto a mano.');
    }
  };

  return (
    <div className="rounded-lg border border-[#E2E8F0] dark:border-white/10 p-4 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <MessageSquare className="w-4 h-4" /> Plantillas de WhatsApp (Meta)
        </div>
        {datos.hay_emisor && datos.emisor_es_waba && (
          <Button size="sm" disabled={enviando} onClick={() => enviarAMeta(null)}>
            {enviando ? 'Enviando…' : 'Enviar faltantes a Meta'}
          </Button>
        )}
      </div>
      <p className="text-xs text-[#64748B] max-w-3xl">{datos.porque_importa}</p>
      <p className="text-xs text-[#64748B] max-w-3xl">
        El botón manda la plantilla a revisión en Meta (estado PENDING). Meta sigue
        aprobando a mano; aquí se actualiza cuando quede APPROVED.
      </p>

      {!datos.hay_emisor && (
        <p className="text-xs text-amber-600">
          Primero elige arriba desde qué negocio sale el WhatsApp de la plataforma.
        </p>
      )}
      {datos.hay_emisor && !datos.emisor_es_waba && (
        <p className="text-xs text-[#64748B] flex items-start gap-1">
          <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          El negocio que elegiste usa un número personal (no WhatsApp Business API), así que estos
          mensajes salen como texto normal y no necesitas dar de alta nada. Esta lista te sirve el
          día que migres a un número Business.
        </p>
      )}

      <div className="space-y-3">
        {(datos.plantillas || []).map((p) => (
          <div key={p.name} className="rounded-md border border-[#E2E8F0] dark:border-white/10 p-3">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
              <div className="flex items-center gap-2">
                {p.lista
                  ? <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  : <AlertTriangle className="w-4 h-4 text-amber-600" />}
                <span className="text-sm font-medium">{p.nombre_del_evento}</span>
                <code className="text-xs text-[#64748B]">{p.name}</code>
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                  p.category === 'UTILITY'
                    ? 'bg-sky-100 text-sky-700'
                    : 'bg-fuchsia-100 text-fuchsia-700'}`}>{p.category}</span>
              </div>
              <div className="flex gap-1">
                {!p.lista && datos.emisor_es_waba && (
                  <Button variant="outline" size="sm" disabled={enviando}
                          onClick={() => enviarAMeta(p.evento)}>
                    Enviar a Meta
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={() => copiar(p)}>
                  <Copy className="w-3.5 h-3.5 mr-1" />
                  {copiada === p.name ? 'Copiado' : 'Copiar'}
                </Button>
              </div>
            </div>
            <p className="text-xs text-[#64748B] mb-2">{p.para_que}</p>
            <pre className="text-xs whitespace-pre-wrap bg-[#F8FAFC] dark:bg-white/5 rounded p-2 text-[#0F172A] dark:text-inherit">
{p.components.find((c) => c.type === 'BODY')?.text}
            </pre>
            <p className="text-[11px] text-[#64748B] mt-2">
              Los datos entran en este orden: {p.parametros_en_orden.join(' → ')}
            </p>
            <p className="text-[11px] mt-1">
              {p.lista
                ? <span className="text-emerald-600">Aprobada en Meta. Ya se usa al enviar.</span>
                : <span className="text-amber-600">
                    Falta darla de alta en Meta{p.estado_en_meta ? ` (estado: ${p.estado_en_meta})` : ''}.
                  </span>}
            </p>
          </div>
        ))}
      </div>

      <details className="text-xs text-[#64748B]">
        <summary className="cursor-pointer font-medium text-[#0F172A] dark:text-inherit">
          Cómo darlas de alta, paso a paso
        </summary>
        <ol className="list-decimal ml-5 mt-2 space-y-1">
          <li>Entra a <strong>business.facebook.com</strong> → Administrador de WhatsApp → Plantillas de mensajes.</li>
          <li>Clic en <strong>Crear plantilla</strong>.</li>
          <li>Elige la <strong>categoría</strong> tal como aparece arriba. Si pones Marketing donde dice Utilidad (o al revés) Meta la rechaza.</li>
          <li>Escribe el <strong>nombre</strong> exacto (el que va en gris, con guiones bajos) e idioma <strong>Español (México)</strong>.</li>
          <li>Pega el cuerpo con el botón <strong>Copiar</strong>. Deja los <code>{'{{1}}'}</code> tal cual: son los huecos donde entran los datos.</li>
          <li>Meta pide un ejemplo de cada hueco — pon un nombre y un monto cualquiera.</li>
          <li>Envía a revisión. Suele tardar de unos minutos a 24 horas. Cuando quede aprobada, aquí aparecerá en verde sola.</li>
        </ol>
      </details>
    </div>
  );
}

/** El único mensaje que se dispara a mano. Manda a todas las cuentas activas. */
function PromocionRapida({ onHecho }) {
  const [mandando, setMandando] = useState(false);

  const mandar = async () => {
    if (!window.confirm('Se mandará la promoción, tal como está guardada, a todas las cuentas activas. ¿Seguimos?')) return;
    setMandando(true);
    try {
      const r = await api.post('/admin/mensajes-del-sistema/promocion', {});
      const saltados = r.data.saltados || [];
      toast.success(`Enviada a ${r.data.enviados} cliente(s).`);
      if (saltados.length) {
        toast.message(`${saltados.length} no la recibieron`, {
          description: saltados.slice(0, 5).map((s) => `${s.negocio}: ${s.porque}`).join(' · '),
        });
      }
      onHecho?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setMandando(false);
    }
  };

  return (
    <Button variant="secondary" onClick={mandar} disabled={mandando}>
      <Megaphone className="w-4 h-4 mr-1" /> {mandando ? 'Enviando…' : 'Mandar a todos los activos'}
    </Button>
  );
}
